import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import Icon from "@/components/ui/Icon";
import { toggleActiveChat } from "@/pages/app/chat/store";
import { useDispatch } from "react-redux";
import useMobileMenu from "@/hooks/useMobileMenu";

const Navmenu = ({ menus, className = "", collapsed = false }) => {
  const [expandedParents, setExpandedParents] = useState({});
  const location = useLocation();
  const locationName = location.pathname.replace(/^\/+/, "");
  const [mobileMenu, setMobileMenu] = useMobileMenu();
  const dispatch = useDispatch();

  const isLocationMatch = (targetLocation) => {
    if (!targetLocation) return false;
    return (
      locationName === targetLocation ||
      locationName.startsWith(`${targetLocation}/`)
    );
  };

  useEffect(() => {
    document.title = `MySway Backoffice | ${locationName || "home"}`;
    dispatch(toggleActiveChat(false));
    if (mobileMenu) {
      setMobileMenu(false);
    }
  }, [location, mobileMenu, dispatch, locationName]);

  useEffect(() => {
    const name = location.pathname.replace(/^\/+/, "");
    const matchChild = (targetLocation) => {
      if (!targetLocation) return false;
      return (
        name === targetLocation || name.startsWith(`${targetLocation}/`)
      );
    };
    const next = {};
    menus.forEach((item) => {
      if (item.isHeadr || !item.child) return;
      if (item.child.some((c) => matchChild(c.childlink))) {
        next[item.title] = true;
      }
    });
    if (Object.keys(next).length) {
      setExpandedParents((prev) => ({ ...prev, ...next }));
    }
  }, [location.pathname, menus]);

  const toggleParent = (title) => {
    setExpandedParents((p) => ({ ...p, [title]: !p[title] }));
  };

  const rowBase =
    "flex items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs transition-colors duration-200";

  const rowCollapsed = collapsed ? "justify-center px-1.5" : "";

  return (
    <nav
      className={`flex flex-col gap-0.5 overflow-y-auto overflow-x-hidden scrollbar-hide px-1.5 pb-6 ${className}`}
    >
      {menus.map((item, i) => {
        if (item.isHeadr) {
          if (collapsed) return null;
          return (
            <div
              key={`hdr-${item.title}-${i}`}
              className="px-2 pb-1 pt-3 first:pt-1"
            >
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500">
                {item.title}
              </p>
            </div>
          );
        }

        const isActive =
          isLocationMatch(item.link) ||
          (item.child &&
            item.child.some((child) =>
              isLocationMatch(child.childlink),
            ));
        const isOpen = !!expandedParents[item.title];

        return (
          <div key={`${item.title}-${i}`} className="relative">
            {!item.child ? (
              <NavLink
                to={item.link}
                title={collapsed ? item.title : undefined}
                className={({ isActive: navActive }) =>
                  [
                    rowBase,
                    rowCollapsed,
                    navActive || isActive
                      ? "bg-teal-50 text-teal-900 shadow-sm ring-1 ring-teal-500/15 dark:bg-teal-950/35 dark:text-teal-100 dark:ring-teal-400/20"
                      : "text-slate-600 hover:bg-stone-100/90 dark:text-slate-400 dark:hover:bg-slate-800/80 dark:hover:text-slate-100",
                  ].join(" ")
                }
              >
                <span
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-base ${
                    isActive
                      ? "bg-white text-teal-700 shadow-sm dark:bg-slate-900 dark:text-teal-300"
                      : "bg-stone-200/70 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
                  }`}
                >
                  <Icon icon={item.icon} />
                </span>
                {!collapsed ? (
                  <>
                    <span className="min-w-0 flex-1 font-medium leading-snug">
                      {item.title}
                    </span>
                    {item.badge ? (
                      <span className="shrink-0 rounded-full bg-rose-500 px-1 py-0.5 text-[9px] font-semibold leading-none text-white">
                        {item.badge}
                      </span>
                    ) : null}
                  </>
                ) : null}
              </NavLink>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => toggleParent(item.title)}
                  title={collapsed ? item.title : undefined}
                  className={[
                    rowBase,
                    rowCollapsed,
                    "w-full touch-manipulation",
                    isActive && !isOpen
                      ? "bg-stone-100/90 text-slate-900 dark:bg-slate-800/60 dark:text-white"
                      : "text-slate-600 hover:bg-stone-100/90 dark:text-slate-400 dark:hover:bg-slate-800/80",
                  ].join(" ")}
                  aria-expanded={isOpen}
                >
                  <span
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-base ${
                      isActive
                        ? "bg-teal-100 text-teal-800 dark:bg-teal-900/50 dark:text-teal-200"
                        : "bg-stone-200/70 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
                    }`}
                  >
                    <Icon icon={item.icon} />
                  </span>
                  {!collapsed ? (
                    <>
                      <span className="min-w-0 flex-1 text-left font-medium leading-snug">
                        {item.title}
                      </span>
                      <Icon
                        icon="heroicons-outline:chevron-down"
                        className={`h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform duration-200 ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    </>
                  ) : (
                    <Icon
                      icon="heroicons-outline:chevron-down"
                      className={`h-3 w-3 shrink-0 text-slate-400 transition-transform duration-200 ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  )}
                </button>
                {isOpen && item.child && (
                  <div
                    className={
                      collapsed
                        ? "mt-0.5 space-y-0.5 border-t border-stone-200/80 pt-1 dark:border-slate-700"
                        : "ml-2 mt-0.5 space-y-0.5 border-l border-stone-200 pl-2 dark:border-slate-700"
                    }
                  >
                    {item.child.map((childItem, j) => (
                      <NavLink
                        key={j}
                        to={childItem.childlink}
                        title={collapsed ? childItem.childtitle : undefined}
                        className={({ isActive: navActive }) =>
                          [
                            collapsed
                              ? "flex items-center justify-center rounded-md py-2 text-[11px] transition-colors"
                              : "flex items-center gap-1.5 rounded-md py-1.5 pr-1.5 text-[11px] transition-colors",
                            navActive ||
                            isLocationMatch(childItem.childlink)
                              ? "font-semibold text-teal-700 dark:text-teal-300"
                              : "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100",
                          ].join(" ")
                        }
                      >
                        {childItem.icon ? (
                          <span
                            className={
                              collapsed
                                ? "flex justify-center text-base opacity-90"
                                : "flex w-6 justify-center text-sm opacity-80"
                            }
                          >
                            <Icon icon={childItem.icon} />
                          </span>
                        ) : null}
                        {!collapsed ? <span>{childItem.childtitle}</span> : null}
                      </NavLink>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
    </nav>
  );
};

export default Navmenu;
